"use client";

import Link from "next/link";
import Image from "next/image";
import { useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { Sun, Moon, Home, Layers, Code2, User, Mail } from "lucide-react";
import RotatingText from "../ui/RotatingText";

const navLinks = [
  { label: "Home",     href: "/",        icon: Home },
  { label: "Services", href: "/services", icon: Layers },
  { label: "Work",     href: "/work",     icon: Code2 },
  { label: "About",    href: "/about",    icon: User },
  { label: "Contact",  href: "/contact",  icon: Mail },
];

const roles = [
  "Websites that convert",
  "Secure online stores",
  "Apps built to scale",
  "Backends that don't break",
  "Code you can trust",
];

function subscribeToTheme(callback: () => void) {
  window.addEventListener("theme-change", callback);
  return () => window.removeEventListener("theme-change", callback);
}
function getThemeSnapshot() {
  return document.documentElement.getAttribute("data-theme") === "dark";
}
function getThemeServerSnapshot() {
  return false;
}

export default function Header() {
  const pathname = usePathname();
  const isDark = useSyncExternalStore(
    subscribeToTheme,
    getThemeSnapshot,
    getThemeServerSnapshot,
  );

  function toggleTheme() {
    const next = !isDark;
    document.documentElement.setAttribute("data-theme", next ? "dark" : "light");
    localStorage.setItem("theme", next ? "dark" : "light");
    window.dispatchEvent(new Event("theme-change"));
  }

  return (
    <>
      {/* ── Top header ─────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 w-full bg-[var(--background)]/90 backdrop-blur-md">
        <div className="relative mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">

          {/* Logo + rotating text */}
          <Link href="/" className="flex flex-col items-start gap-0.5 shrink-0" aria-label="Webdev V2 — Home">
            <div className="relative h-8 w-[110px]">
              <Image
                src="/logo/logo3.png"
                alt="Webdev V2"
                fill
                priority
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                className="logo-img object-contain object-left"
              />
            </div>
            <span className="text-xs font-medium text-[var(--accent)] leading-none">
              <RotatingText items={roles} interval={2800} />
            </span>
          </Link>

          {/* Desktop nav — absolutely centred, never squeezes logo */}
          <nav className="absolute left-1/2 hidden -translate-x-1/2 md:flex items-center gap-1">
            {navLinks.map(({ label, href, icon: Icon }) => {
              const isActive = href === "/" ? pathname === "/" : pathname.startsWith(href);
              return (
              <Link
                key={href}
                href={href}
                className={[
                  "group relative flex items-center gap-1.5 rounded-lg border border-transparent px-3 py-2 text-sm font-medium transition-all duration-200",
                  isActive
                    ? "text-[var(--accent)]"
                    : "text-[var(--foreground-muted)] hover:border-[var(--border)] hover:bg-[var(--background-alt)] hover:text-[var(--foreground)]",
                ].join(" ")}
              >
                <Icon size={14} className={isActive ? "text-[var(--accent)]" : "transition-colors duration-200 group-hover:text-[var(--accent)]"} />
                {label}
                {/* Active underline */}
                {isActive && (
                  <span className="absolute bottom-0.5 left-3 right-3 h-[2px] rounded-full bg-[var(--accent)]" />
                )}
              </Link>
              );
            })}
          </nav>

          {/* Theme toggle — always visible */}
          <button
            onClick={toggleTheme}
            suppressHydrationWarning
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--border)] text-[var(--foreground-muted)] transition-colors duration-200 hover:border-[var(--accent)] hover:text-[var(--accent)]"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </header>

      {/* ── Mobile bottom nav ──────────────────────────────────── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 flex items-stretch justify-around border-t border-[var(--border)] bg-[var(--background)]/95 backdrop-blur-md">
        {navLinks.map(({ label, href, icon: Icon }) => {
          const isActive = href === "/" ? pathname === "/" : pathname.startsWith(href);
          return (
          <Link
            key={href}
            href={href}
            className={[
              "group relative flex flex-1 flex-col items-center gap-0.5 py-3 transition-colors duration-200",
              isActive ? "text-[var(--accent)]" : "text-[var(--foreground-muted)] hover:text-[var(--accent)] active:text-[var(--accent)]",
            ].join(" ")}
          >
            {/* Active top line */}
            {isActive && (
              <span className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-[2px] rounded-full bg-[var(--accent)]" />
            )}
            <Icon size={20} className={isActive ? "" : "transition-transform duration-200 group-hover:scale-110"} />
            <span className="text-[10px] font-medium tracking-wide">{label}</span>
          </Link>
          );
        })}
      </nav>
    </>
  );
}
